import { motion } from 'framer-motion';
import {
  ComputerDesktopIcon,
  CpuChipIcon,
  UsersIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';

function GamingHero() {
  return (
    <section className="relative overflow-hidden px-6 pb-40 pt-32">
      {/* Background neon blobs and grids */}
      <div className="absolute inset-0 -z-10">
        {/* Radial glow - adapts to theme */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(236,72,153,0.18),transparent_60%)] dark:bg-[radial-gradient(circle_at_70%_30%,rgba(236,72,153,0.25),transparent_60%)]" />
        {/* Grid - visible in both themes */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(236,72,153,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(236,72,153,0.08)_1px,transparent_1px)] bg-[size:64px_64px] dark:bg-[linear-gradient(to_right,rgba(236,72,153,0.12)_1px,transparent_1px),linear-gradient(to_bottom,rgba(236,72,153,0.12)_1px,transparent_1px)]" />
      </div>

      <div className="mx-auto max-w-6xl text-center">
        {/* Animated Caption */}
        <motion.span
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-4 block text-sm font-medium uppercase tracking-wider text-rose-500 dark:text-rose-400"
        >
          Next-Gen XR & Gaming Engineering
        </motion.span>

        {/* Main Heading */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="mb-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl"
        >
          <span className="animate-gradientFlow block bg-gradient-to-r from-pink-500 via-rose-500 to-purple-600 bg-clip-text text-transparent dark:from-pink-400 dark:via-rose-400 dark:to-purple-400">
            Immersive Worlds
          </span>
          <span className="mt-2 block text-gray-900 dark:text-white/90">
            Engineered for Gamers & XR Experiences
          </span>
        </motion.h1>

        {/* Subheading */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="mx-auto max-w-3xl text-lg leading-relaxed text-gray-600 sm:text-xl dark:text-white/70"
        >
          We craft hyper-realistic XR environments and gaming systems that push the boundaries of
          interactivity, immersion, and performance. From AI-driven NPCs to multiplayer
          architectures, we engineer experiences players remember.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-12 flex flex-col flex-wrap justify-center gap-4 sm:flex-row sm:gap-6"
        >
          {/* Key Highlights */}
          <div className="flex items-center gap-3 rounded-full border border-gray-200 bg-white/80 px-5 py-3 shadow-lg backdrop-blur-lg transition-all duration-300 hover:scale-105 hover:shadow-xl dark:border-white/20 dark:bg-white/10">
            <ComputerDesktopIcon className="h-6 w-6 text-pink-500 dark:text-pink-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              Immersive 3D Worlds
            </span>
          </div>

          <div className="flex items-center gap-3 rounded-full border border-gray-200 bg-white/80 px-5 py-3 shadow-lg backdrop-blur-lg transition-all duration-300 hover:scale-105 hover:shadow-xl dark:border-white/20 dark:bg-white/10">
            <CpuChipIcon className="h-6 w-6 text-rose-500 dark:text-rose-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              High-Performance Engines
            </span>
          </div>

          <div className="flex items-center gap-3 rounded-full border border-gray-200 bg-white/80 px-5 py-3 shadow-lg backdrop-blur-lg transition-all duration-300 hover:scale-105 hover:shadow-xl dark:border-white/20 dark:bg-white/10">
            <UsersIcon className="h-6 w-6 text-purple-500 dark:text-purple-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              Multiplayer & Social XR
            </span>
          </div>

          <div className="flex items-center gap-3 rounded-full border border-gray-200 bg-white/80 px-5 py-3 shadow-lg backdrop-blur-lg transition-all duration-300 hover:scale-105 hover:shadow-xl dark:border-white/20 dark:bg-white/10">
            <Cog6ToothIcon className="h-6 w-6 text-pink-500 dark:text-pink-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              AI-Driven Interactions
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function GamingExperience() {
  const experiences = [
    {
      title: 'User-Focused Design',
      description:
        'We craft game mechanics and XR interactions centered around player engagement, accessibility, and retention.',
      icon: UserGroupIcon,
      color: 'from-pink-400 to-rose-400',
    },
    {
      title: 'High-Performance Architecture',
      description:
        'Our engines are optimized for speed, stability, and real-time rendering across devices and platforms.',
      icon: CpuChipIcon,
      color: 'from-purple-400 to-indigo-400',
    },
    {
      title: 'AI-Powered Worlds',
      description:
        'Dynamic NPCs, intelligent gameplay systems, and procedural content generation make every experience unique.',
      icon: BoltIcon,
      color: 'from-yellow-400 to-orange-400',
    },
    {
      title: 'Multiplayer & Social XR',
      description:
        'Seamless multiplayer networking and XR social features connect users in interactive virtual environments.',
      icon: UsersIcon,
      color: 'from-cyan-400 to-teal-400',
    },
  ];

  return (
    <section className="mx-auto max-w-6xl px-6 py-24">
      <div className="mb-16 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-3xl font-extrabold sm:text-4xl"
        >
          <span className="animate-gradientFlow bg-gradient-to-r from-pink-400 via-rose-400 to-purple-400 bg-clip-text text-transparent">
            How We Build XR & Gaming Experiences
          </span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-white/70"
        >
          From concept to deployment, our team blends engineering, AI, and creative design to
          deliver next-level immersive experiences.
        </motion.p>
      </div>

      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {experiences.map((exp) => {
          const Icon = exp.icon;
          return (
            <motion.div
              key={exp.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="group relative flex flex-col items-center gap-4 rounded-3xl border border-gray-200/50 bg-white/95 p-8 shadow-xl backdrop-blur-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl dark:border-white/20 dark:bg-white/10"
            >
              <div
                className={`relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-tr ${exp.color} text-white shadow-lg transition-all duration-500 group-hover:scale-110 group-hover:shadow-xl`}
              >
                <Icon className="h-8 w-8" />
                <span className="absolute -inset-2 animate-pulse rounded-full bg-gradient-to-r from-white/30 via-white/20 to-white/30 opacity-40 blur-xl group-hover:opacity-60" />
              </div>
              <h3 className="text-center text-xl font-bold text-gray-900 transition-colors duration-300 dark:text-white">
                {exp.title}
              </h3>
              <p className="text-center text-sm leading-relaxed text-gray-600 transition-colors duration-300 sm:text-base dark:text-gray-300">
                {exp.description}
              </p>

              {/* Subtle hover glow effect */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-pink-500/5 via-transparent to-purple-500/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

export default function Gaming() {
  return (
    <div className="mx-auto sm:px-6 lg:px-8">
      <GamingHero />
      <GamingExperience />
    </div>
  );
}
